import Phaser from 'phaser';
import { COLORS, UI, DEPTH } from '../constants';

export interface UISliderConfig {
  x: number;
  y: number;
  width?: number;
  height?: number;
  min?: number;
  max?: number;
  value?: number;
  label?: string;
  onChange?: (value: number) => void;
}

export class UISlider extends Phaser.GameObjects.Container {
  private track: Phaser.GameObjects.Rectangle;
  private fill: Phaser.GameObjects.Rectangle;
  private handle: Phaser.GameObjects.Arc;
  private labelText: Phaser.GameObjects.Text | null = null;
  private valueText: Phaser.GameObjects.Text;

  private min: number;
  private max: number;
  private currentValue: number;
  private sliderWidth: number;
  private sliderHeight: number;
  private isDragging: boolean = false;
  private isSelected: boolean = false;
  private onChangeCallback?: (value: number) => void;

  constructor(scene: Phaser.Scene, config: UISliderConfig) {
    super(scene, config.x, config.y);

    this.sliderWidth = config.width ?? UI.SLIDER_WIDTH;
    this.sliderHeight = config.height ?? UI.SLIDER_HEIGHT;
    this.min = config.min ?? 0;
    this.max = config.max ?? 1;
    this.currentValue = config.value ?? this.min;
    this.onChangeCallback = config.onChange;

    // Label (if provided)
    if (config.label) {
      this.labelText = scene.add.text(-this.sliderWidth / 2, -25, config.label, {
        fontSize: '16px',
        color: '#ffffff',
      });
      this.labelText.setOrigin(0, 0.5);
      this.add(this.labelText);
    }

    // Track background
    this.track = scene.add.rectangle(0, 0, this.sliderWidth, this.sliderHeight, COLORS.BG_DARK);
    this.track.setStrokeStyle(2, COLORS.BG_LIGHT);

    // Fill
    const fillWidth = this.valueToPosition(this.currentValue) + this.sliderWidth / 2;
    this.fill = scene.add.rectangle(
      -this.sliderWidth / 2 + fillWidth / 2,
      0,
      fillWidth,
      this.sliderHeight - 4,
      COLORS.PRIMARY
    );

    // Handle
    const handleX = this.valueToPosition(this.currentValue);
    this.handle = scene.add.circle(handleX, 0, this.sliderHeight / 2 + 4, COLORS.PRIMARY_LIGHT);
    this.handle.setStrokeStyle(2, 0xffffff);

    // Value text
    this.valueText = scene.add.text(this.sliderWidth / 2 + 20, 0, this.formatValue(this.currentValue), {
      fontSize: '16px',
      color: '#ffffff',
    });
    this.valueText.setOrigin(0, 0.5);

    this.add([this.track, this.fill, this.handle, this.valueText]);
    this.setSize(this.sliderWidth + 60, this.sliderHeight + 50);
    this.setDepth(DEPTH.UI_ELEMENTS);

    // Make track interactive for clicking
    this.track.setInteractive({ useHandCursor: true });
    this.handle.setInteractive({ useHandCursor: true, draggable: true });

    // Track click
    this.track.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.handlePointerPosition(pointer.x);
    });

    // Handle drag
    scene.input.on('drag', (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.GameObject, dragX: number) => {
      if (gameObject === this.handle) {
        this.handleDrag(dragX);
      }
    });

    scene.add.existing(this);
  }

  private valueToPosition(value: number): number {
    const normalized = (value - this.min) / (this.max - this.min);
    return normalized * this.sliderWidth - this.sliderWidth / 2;
  }

  private positionToValue(position: number): number {
    const normalized = (position + this.sliderWidth / 2) / this.sliderWidth;
    return this.min + normalized * (this.max - this.min);
  }

  private handlePointerPosition(worldX: number): void {
    const localX = worldX - this.x;
    const clampedX = Phaser.Math.Clamp(localX, -this.sliderWidth / 2, this.sliderWidth / 2);
    this.setValue(this.positionToValue(clampedX));
  }

  private handleDrag(dragX: number): void {
    const clampedX = Phaser.Math.Clamp(dragX, -this.sliderWidth / 2, this.sliderWidth / 2);
    this.setValue(this.positionToValue(clampedX));
  }

  private formatValue(value: number): string {
    return Math.round(value * 100) + '%';
  }

  private updateVisuals(): void {
    const handleX = this.valueToPosition(this.currentValue);
    this.handle.setPosition(handleX, 0);

    const fillWidth = handleX + this.sliderWidth / 2;
    this.fill.setPosition(-this.sliderWidth / 2 + fillWidth / 2, 0);
    this.fill.setSize(Math.max(0, fillWidth), this.sliderHeight - 4);

    this.valueText.setText(this.formatValue(this.currentValue));

    // Selection highlight
    if (this.isSelected) {
      this.track.setStrokeStyle(3, COLORS.ACCENT);
      this.handle.setFillStyle(COLORS.ACCENT);
    } else {
      this.track.setStrokeStyle(2, COLORS.BG_LIGHT);
      this.handle.setFillStyle(COLORS.PRIMARY_LIGHT);
    }
  }

  getValue(): number {
    return this.currentValue;
  }

  setValue(value: number): void {
    this.currentValue = Phaser.Math.Clamp(value, this.min, this.max);
    this.updateVisuals();
    if (this.onChangeCallback) {
      this.onChangeCallback(this.currentValue);
    }
  }

  // Increment/decrement for gamepad/keyboard
  increment(step: number = 0.1): void {
    this.setValue(this.currentValue + step);
  }

  decrement(step: number = 0.1): void {
    this.setValue(this.currentValue - step);
  }

  // Selection for navigation
  setSelected(selected: boolean): void {
    this.isSelected = selected;
    this.updateVisuals();
  }

  setOnChange(callback: (value: number) => void): void {
    this.onChangeCallback = callback;
  }
}
